import React, { useState } from "react";
import Popover from "@mui/material/Popover";
import { MdAttachFile, MdOutlineStickyNote2 } from "react-icons/md";
import {
  checkFileType,
  convertTime,
} from "../../../../../../../helpers/helpers";
import {
  isVideoAttachment,
  VideoTile,
} from "../../../../../../../Components/AttachmentVideo/AttachmentVideo";

const clean = (value) => String(value ?? "").trim();

// The order's notes, newest first, after the order's own note (customer_note,
// typed when the order was created), which comes first.
export const orderNotes = (order) => {
  const own = clean(order?.customer_note);
  const added = [...(order?.order_notes ?? [])].sort((a, b) =>
    String(b?.created_at ?? "").localeCompare(String(a?.created_at ?? "")),
  );
  return own ? [{ id: "order-note", own: true, note: own }, ...added] : added;
};

const statusLabel = (status) =>
  clean(status)
    .toLowerCase()
    .replace(/\b\w/g, (char) => char.toUpperCase());

const isImage = (file) => checkFileType(file?.file ?? "") === "Image";
// The photos on these notes (the pictures attached to them), in the notes'
// order.
export const notePhotos = (notes) =>
  (notes ?? []).flatMap((note) =>
    (note?.order_attachments ?? []).filter(isImage),
  );
const PHOTO_SMALL =
  "h-[40px] w-[40px] rounded-[4px] border-[1px] border-ui-border object-cover";
const PHOTO_LARGE =
  "h-[96px] w-[96px] rounded-[6px] border-[1px] border-ui-border object-cover sm:h-[112px] sm:w-[112px]";

/**
 * One note as the order view lists it: who and when, the status it was saved
 * with ("Order note" for the order's own), what was written, and its photos
 * and files, each opening full size. `large`: photos big enough to check from
 * a phone (a stop's photos panel).
 */
export const NoteEntry = ({ note, large = false }) => {
  const files = note?.order_attachments ?? [];
  return (
    <article
      className={`grid gap-[3px] border-b-[1px] border-ui-border-subtle px-[12px] py-[8px] last:border-b-0 ${
        note.own ? "bg-state-warning-bg text-state-warning-text" : ""
      }`}
    >
      <div className="flex flex-wrap items-baseline gap-x-[8px] text-[11px]">
        {note.own ? (
          <b>Order note</b>
        ) : (
          <>
            <b className="text-ui-text">{note?.created_by?.username ?? "-"}</b>
            <span className="text-ui-text-subtle">
              {convertTime(note?.created_at)}
            </span>
            {clean(note?.fulfillment_order_status) && (
              <span className="text-ui-text-muted">
                · {statusLabel(note.fulfillment_order_status)}
              </span>
            )}
          </>
        )}
      </div>
      {clean(note?.note) && (
        <p className="m-[0px] whitespace-pre-wrap break-words">{note.note}</p>
      )}
      {files.length > 0 && (
        <div className="flex flex-wrap gap-[6px]">
          {files.map((file, fileIndex) =>
            isVideoAttachment(file) ? (
              <VideoTile
                key={file?.id ?? fileIndex}
                src={file?.file}
                label={`Video ${fileIndex + 1}`}
                className={
                  large
                    ? "h-[96px] w-[96px] rounded-[6px] sm:h-[112px] sm:w-[112px]"
                    : "h-[40px] w-[40px] rounded-[4px]"
                }
              />
            ) : isImage(file) ? (
              <a
                key={file?.id ?? fileIndex}
                href={file?.file}
                target="_blank"
                rel="noopener noreferrer"
              >
                <img
                  src={file?.file}
                  alt={`Attachment ${fileIndex + 1}`}
                  loading="lazy"
                  className={large ? PHOTO_LARGE : PHOTO_SMALL}
                />
              </a>
            ) : (
              <a
                key={file?.id ?? fileIndex}
                href={file?.file}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-[3px] text-[11px] text-brand hover:underline"
              >
                <MdAttachFile className="h-[12px] w-[12px]" />
                File {fileIndex + 1}
              </a>
            ),
          )}
        </div>
      )}
    </article>
  );
};

/**
 * "Notes N" on a stop: the order's own note and the notes added to it
 * (comments and status changes), opened in a small preview — author, time,
 * the status it was saved with, the text, and its photos and files, as the
 * order view lists them. Nothing shows when the order has no notes.
 */
const OrderNotes = ({ order }) => {
  const [anchor, setAnchor] = useState(null);
  const notes = orderNotes(order);
  if (!notes.length) return null;

  return (
    <>
      <button
        type="button"
        aria-haspopup="dialog"
        aria-expanded={Boolean(anchor)}
        aria-label={`Notes on #${order?.id}: ${notes.length}`}
        onClick={(event) => setAnchor(event.currentTarget)}
        className="inline-flex shrink-0 items-center gap-[4px] whitespace-nowrap rounded-full border-[1px] border-ui-border bg-ui-surface px-[8px] py-[1px] text-[11px] font-semibold text-ui-text-muted hover:border-brand hover:text-brand print:hidden"
      >
        <MdOutlineStickyNote2
          aria-hidden="true"
          className="h-[12px] w-[12px]"
        />
        Notes
        <span className="rounded-full bg-state-neutral-bg px-[5px] text-[10px] leading-[15px]">
          {notes.length}
        </span>
      </button>
      <Popover
        open={Boolean(anchor)}
        anchorEl={anchor}
        onClose={() => setAnchor(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
        sx={{ zIndex: 1400 }}
        PaperProps={{
          role: "dialog",
          "aria-label": `Notes on #${order?.id}`,
          className:
            "w-[340px] max-w-[92vw] max-h-[380px] overflow-auto rounded-[9px] border-[1px] border-ui-border bg-ui-surface font-manrope text-[12px] text-ui-text shadow-ui-md",
        }}
      >
        <div className="sticky top-0 border-b-[1px] border-ui-border bg-ui-surface-sunken px-[12px] py-[7px] text-[11.5px] font-bold">
          Notes · #{order?.id}
        </div>
        {notes.map((note, index) => (
          <NoteEntry key={note?.id ?? index} note={note} />
        ))}
      </Popover>
    </>
  );
};

export default OrderNotes;
